import moment from "moment";
import React from "react";
import { withTranslation } from "react-i18next";
import submit from "submit-form";
import TablistNav from "../../../parts/inputs/TablistNav";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../parts/SvgIcon";
import { RillContext } from "../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../utils/get-avatar-url";
import ChooseCoach from "../../dashboard/settings/manage-coaches/ChooseCoach";
import {
  getRoleNameById,
  getUsersAssignableAsCoach,
} from "../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { canLoadAllOrganizationAthletesForTasks } from "../../../utils/organization-permissions-helpers";
import EmptyTasks from "./components/EmptyTasks";
import {
  CHECKIN,
  COMMENTARY,
  OTHER,
  PROGRAMMING,
  VIDEO,
} from "./task-constants";

const videoReviewModalHref = (task, organizationCoachParam) => {
  const v = task.video;
  if (!v) return null;
  let href = `/actions/refresh?modal[type]=exercise-set-video&originalId=${encodeURIComponent(
    v.videoOriginal
  )}&athleteId=${task.athlete}`;
  if (v.uploaderId) {
    href += `&uploaderId=${encodeURIComponent(v.uploaderId)}`;
  }
  if (v.exerciseId != null && v.setIndex != null && v.individualSet != null) {
    const utcDate =
      v.utcDate != null
        ? v.utcDate
        : parseInt(moment.utc(task.date).format("YYYYMMDD"), 10);
    href += `&exerciseToEdit=${v.exerciseId}&setIndex=${v.setIndex}&videoIndex=${v.individualSet}&utcDate=${utcDate}`;
  }
  if (v.threadId) {
    href += `&threadId=${v.threadId}`;
  }
  return href + organizationCoachParam;
};

const showDueDate = (taskDate, t) => {
  const today = moment.utc().startOf("day");
  const yesterday = today.clone().subtract(1, "d");
  const tomorrow = today.clone().add(1, "d");
  const isToday = taskDate.isSame(today, "d");
  const isYesterday = taskDate.isSame(yesterday, "d");
  const isTomorrow = taskDate.isSame(tomorrow, "d");
  return (
    <span>
      {isToday && t("tasks.view.today")}
      {isYesterday && t("tasks.view.yesterday")}
      {isTomorrow && t("tasks.view.tomorrow")}
      {!isToday && !isYesterday && !isTomorrow && taskDate.fromNow()}
    </span>
  );
};

const showTaskList = (
  tasks,
  currentTab,
  handleSelectTask,
  selectedTasks,
  organizationCoach,
  _screenHeight,
  isAllowToViewAllOrganizationsCoaches,
  activeUserId,
  t
) => {
  return tasks.map((task) => {
    const taskDate = moment.utc(task.date).startOf("day");
    const isSelected = selectedTasks.includes(task._id);

    const organizationCoachParam = organizationCoach
      ? `&organizationCoach=${organizationCoach}`
      : isAllowToViewAllOrganizationsCoaches &&
          task.user &&
          task.user !== activeUserId
        ? `&organizationCoach=${task.user}`
        : "";

    const checkInComment = task.description.includes("check-in");

    return (
      <tr className="task task-individual" key={task._id}>
        <td className="col select-col">
          <SvgIcon
            onClickFn={(e) => handleSelectTask(e, task._id)}
            src={
              isSelected
                ? "/img/new-icons/checked-square.svg"
                : "/img/new-icons/unchecked-square.svg"
            }
            noFilterOnLight={isSelected}
          />
        </td>
        <td className="col name">
          {task.athleteInfo && (
            <a
              href={`/athlete-portal/calendar/?athlete=${task.athleteInfo._id}${organizationCoachParam}`}
              className="image"
            >
              <img
                src={getAvatarUrl(task.athleteInfo)}
                alt=""
                loading="lazy"
                className="profile-picture w-8 h-8 rounded-full mr-2"
              />
              {task.athleteInfo.name}
            </a>
          )}
        </td>
        <td className="col description">{task.description}</td>

        <td className="col date">
          {(currentTab === VIDEO ||
            currentTab === COMMENTARY ||
            currentTab === CHECKIN) &&
            task.athleteInfo && (
              <div className="flex gap-2 items-center">
                <span>{showDueDate(taskDate, t)}</span>
                <a
                  href={
                    currentTab === CHECKIN || checkInComment
                      ? `/athlete-portal/metrics/check-in-summary?athlete=${
                          task.athleteInfo._id
                        }&checkInDate=${taskDate
                          .clone()
                          .format("YYYY-MM-DD")}&navFrom=tasks` +
                        organizationCoachParam
                      : `/athlete-portal/calendar/?athlete=${
                          task.athleteInfo._id
                        }&view=day&date=${taskDate
                          .clone()
                          .format("YYYY-MM-DD")}${organizationCoachParam}`
                  }
                  role="button"
                  data-button-type="outline"
                  cl
                >
                  <span>{t("tasks.view.view")}</span>
                </a>
              </div>
            )}
          {(currentTab === PROGRAMMING ||
            currentTab === OTHER ||
            !task.athleteInfo) &&
            showDueDate(taskDate, t)}
        </td>
        {currentTab === VIDEO && (
          <td className="video-task-col-content">
            {task.video && (
              <span className="video">
                <a
                  className="task-video"
                  href={videoReviewModalHref(task, organizationCoachParam)}
                >
                  <SvgIcon
                    src="/img/new-icons/play-with-gradient.svg"
                    noFilterOnLight={true}
                  />
                </a>
              </span>
            )}
          </td>
        )}
        <td className="col edit-col">
          <a
            role="button"
            data-button-type="secondary"
            data-icon
            href={
              organizationCoach
                ? `/coaching-portal/tasks/?modal[type]=edit-task&id=${task._id}&tab=${task.type}&organizationCoach=${organizationCoach}`
                : `/coaching-portal/tasks/?modal[type]=edit-task&id=${task._id}&tab=${task.type}`
            }
            className="w-fit"
          >
            <SvgIcon
              src="/img/new-icons/edit-pencil.svg"
              alt={t("tasks.view.editTaskAlt")}
            />
          </a>
        </td>
      </tr>
    );
  });
};

class View extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      selectedTasks: [],
      screenHeight: this.isBrowser() ? window.innerHeight : null,
      remSize: this.isBrowser() ? this.getRemSize() : null,
    };
    this.handleSelectTask = this.handleSelectTask.bind(this);
    this.handleDeselectAll = this.handleDeselectAll.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleMarkCompleted = this.handleMarkCompleted.bind(this);
    this.selectOrganizationCoach = this.selectOrganizationCoach.bind(this);
  }
  componentDidMount() {
    if (this.isBrowser()) {
      window.addEventListener("resize", this.handleResize);
    }
  }

  componentWillUnmount() {
    if (this.isBrowser()) {
      window.removeEventListener("resize", this.handleResize);
    }
  }

  handleResize = () => {
    this.setState({
      screenHeight: window.innerHeight,
    });
    this.updateRemSize();
  };
  componentDidUpdate(prevProps) {
    const { completed, tab } = this.props.query;
    if (
      prevProps.query.completed !== completed ||
      prevProps.query.tab !== tab
    ) {
      this.handleDeselectAll();
    }
  }

  handleSelectTask(e, id) {
    e.preventDefault();
    const selectedTasks = this.state.selectedTasks.slice();
    if (selectedTasks.indexOf(id) > -1) {
      selectedTasks.splice(selectedTasks.indexOf(id), 1);
    } else {
      selectedTasks.push(id);
    }

    this.setState({ selectedTasks });
  }

  handleDeselectAll(e) {
    if (e) e.preventDefault();
    this.setState({ selectedTasks: [] });
  }

  handleSelectAll(e) {
    e.preventDefault();
    const { tasks } = this.context.locals;
    const selectedTasks = [];

    tasks.thisWeek.forEach((task) => {
      selectedTasks.push(task._id);
    });
    tasks.upcoming.forEach((task) => {
      selectedTasks.push(task._id);
    });

    this.setState({ selectedTasks });
  }

  handleMarkCompleted(e) {
    e.preventDefault();
    const { selectedTasks } = this.state;
    const { tab } = this.props.query;
    submit(`/coaching-portal/tasks/complete`, {
      method: "POST",
      body: {
        tasks: selectedTasks,
        tab,
      },
    });
    this.handleDeselectAll();
  }
  selectOrganizationCoach(e, coach) {
    e.stopPropagation();
    e.preventDefault();

    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        organizationCoach: coach._id,
        filterGroup: "",
      },
    });
  }
  isBrowser() {
    return typeof window !== "undefined";
  }
  getRemSize() {
    // Get the computed value of 1rem in pixels
    const computedRem = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    return computedRem;
  }
  updateRemSize() {
    this.setState({
      remSize: this.getRemSize(),
    });
  }
  render() {
    const { locals } = this.context;
    const { selectedTasks, screenHeight, remSize } = this.state;

    const {
      query,
      tasks,
      activeUser,
      organizationRoles = [],
      usersOrganization = {
        organizationActiveUsers: [],
      },
      coachOrganizationInformation,
    } = locals;
    console.log("tasks", tasks);
    const activeUserId = activeUser._id;
    const { organizationActiveUsers = [] } = usersOrganization;
    const organizationCoach = query.organizationCoach || undefined;
    const { athletesGroups } = coachOrganizationInformation ?? activeUser;
    const tab = query.tab || PROGRAMMING;
    const completed = query.completed || false;
    const { thisWeek, upcoming } = tasks;
    const thisWeekLength = thisWeek?.length;
    const upcomingLength = upcoming?.length;
    const referAsClients = Boolean(activeUser.referAsClients);
    //Get the name of the organization's role of the current user
    const organizationRoleName = activeUser.organizationRoleId
      ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
      : "Coach";
    //The user is allow to see al the athletes of the coaches
    const isAllowToViewAllOrganizationsCoaches =
      canLoadAllOrganizationAthletesForTasks(organizationRoleName);
    // A Team Coach never holds clients, so picking it here would always land
    // on an empty list — same reason it's filtered out of every "assign a
    // coach" picker.
    const assignableOrganizationActiveUsers = getUsersAssignableAsCoach(
      organizationActiveUsers
    );

    const athletesArrayGroups = athletesGroups || [];

    const t = this.props.t;
    const allRoleLabel = referAsClients
      ? t("tasks.view.allClients")
      : t("tasks.view.allAthletes");
    const allOrgRoleLabel = referAsClients
      ? t("tasks.view.allOrganizationClients")
      : t("tasks.view.allOrganizationAthletes");

    // A Team Coach has no personal roster to fall back to, so "all" is
    // already org-wide for them (see the isTeamCoach default in
    // tasks-controller.js's sibling, coaching-dashboard-controller.js, and
    // the endpoint switch in load/tasks.js) — the separate "All
    // Organization" option would be a same-result duplicate, same reasoning
    // as the athlete list's hideOrganizationFilter.
    const isTeamCoach = organizationRoleName === "Team Coach";
    const groupOptions =
      isAllowToViewAllOrganizationsCoaches && !isTeamCoach
        ? [
            { text: allRoleLabel, value: "all" },
            {
              text: allOrgRoleLabel,
              value: "organization",
              href: `/actions/refresh?tab=${tab}&filterGroup=organization`,
            },
          ]
        : [{ text: allRoleLabel, value: "all" }];

    const sizeOfTable = screenHeight - (270 + 44 + 98) * (remSize / 16);
    const totalRows = Math.floor(sizeOfTable / (3 * remSize));
    // Spacer rows hold no data, so carry their own stable keys.
    const emptyRowCount = Math.max(
      totalRows - thisWeek.length - upcoming.length,
      0
    );
    const emptyRows = Array.from(
      { length: emptyRowCount },
      (_, i) => `empty-row-${i}`
    );

    return (
      <main className="coaching-tasks">
        <header>
          <TablistNav
            name="tab"
            options={[
              {
                text: t("tasks.view.tabPrograms"),
                value: PROGRAMMING,
                href: organizationCoach
                  ? `/coaching-portal/tasks?tab=${PROGRAMMING}&completed=${completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks?tab=${PROGRAMMING}&completed=${completed}`,
              },
              {
                text: t("tasks.view.tabVideos"),
                value: VIDEO,
                href: organizationCoach
                  ? `/coaching-portal/tasks?tab=${VIDEO}&completed=${completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks?tab=${VIDEO}&completed=${completed}`,
              },
              {
                text: t("tasks.view.tabComments"),
                value: COMMENTARY,
                href: organizationCoach
                  ? `/coaching-portal/tasks?tab=${COMMENTARY}&completed=${completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks?tab=${COMMENTARY}&completed=${completed}`,
              },
              {
                text: t("tasks.view.tabCheckIns"),
                value: CHECKIN,
                href: organizationCoach
                  ? `/coaching-portal/tasks?tab=${CHECKIN}&completed=${completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks?tab=${CHECKIN}&completed=${completed}`,
              },
              {
                text: t("tasks.view.tabOther"),
                value: OTHER,
                href: organizationCoach
                  ? `/coaching-portal/tasks?tab=${OTHER}&completed=${completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks?tab=${OTHER}&completed=${completed}`,
              },
            ]}
          />
          <div>
            {isAllowToViewAllOrganizationsCoaches && (
              <ChooseCoach
                activeUser={activeUser}
                organizationActiveUsers={assignableOrganizationActiveUsers}
                isFrom="list"
                selectedCoach={
                  assignableOrganizationActiveUsers.find(
                    (obj) =>
                      obj._id.toString() === query.organizationCoach?.toString()
                  ) || assignableOrganizationActiveUsers[0]
                }
                onSelectCoach={this.selectOrganizationCoach}
              />
            )}
            <a
              role="button"
              data-button-type="outline"
              disabled={selectedTasks.length > 0}
              href={
                organizationCoach
                  ? `/coaching-portal/tasks/?tab=${tab}&completed=${!completed}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks/?tab=${tab}&completed=${!completed}`
              }
            >
              <span>
                {completed
                  ? t("tasks.view.showNotCompleted")
                  : t("tasks.view.showCompleted")}
              </span>
            </a>
            <a
              role="button"
              data-button-type="primary"
              href={
                organizationCoach
                  ? `/coaching-portal/tasks/?modal[type]=add-task&tab=${tab}&organizationCoach=${organizationCoach}`
                  : `/coaching-portal/tasks/?modal[type]=add-task&tab=${tab}`
              }
              disabled={selectedTasks.length > 0}
            >
              {t("tasks.view.addTask")}
            </a>
          </div>
        </header>

        <section>
          <div className="athletes-groups-wrapper">
            <div>
              {thisWeekLength > 0 && (
                <div className="chip info">
                  {t("tasks.view.thisWeek", { count: thisWeekLength })}
                </div>
              )}
              {((athletesArrayGroups && athletesArrayGroups.length > 0) ||
                isAllowToViewAllOrganizationsCoaches) && (
                <ToggleLinksInput
                  name="filterGroup"
                  options={[
                    ...groupOptions,
                    ...athletesArrayGroups.map((athleteGroup) => ({
                      text: athleteGroup.name,
                      value: athleteGroup.name,
                    })),
                  ]}
                />
              )}
            </div>
            {selectedTasks.length > 0 && (
              <button
                type="button"
                data-button-type="primary"
                className="complete-tasks"
                onClick={this.handleMarkCompleted}
              >
                {completed
                  ? t("tasks.view.markNotComplete")
                  : t("tasks.view.markComplete")}
              </button>
            )}
          </div>

          {/* select and deselect all */}
          {thisWeekLength > 0 || upcomingLength > 0 ? (
            <section data-borders-inside>
              <table>
                <thead>
                  <tr className="task-description">
                    <th className="col select-col">
                      {(thisWeekLength > 0 || upcomingLength > 0) && (
                        <SvgIcon
                          src={
                            selectedTasks.length ===
                            thisWeekLength + upcomingLength
                              ? "/img/new-icons/checked-square.svg"
                              : "/img/new-icons/unchecked-square.svg"
                          }
                          onClickFn={
                            selectedTasks.length ===
                            thisWeekLength + upcomingLength
                              ? this.handleDeselectAll
                              : this.handleSelectAll
                          }
                          noFilterOnLight={
                            selectedTasks.length ===
                            thisWeekLength + upcomingLength
                          }
                        />
                      )}
                    </th>

                    <th className="col name">
                      {referAsClients
                        ? t("tasks.view.columnClient")
                        : t("tasks.view.columnAthlete")}
                    </th>
                    <th className="col description">
                      {t("tasks.view.columnTasks")}
                    </th>

                    <th className="col date">
                      {tab === VIDEO || tab === COMMENTARY
                        ? t("tasks.view.columnWorkoutDate")
                        : tab === CHECKIN
                          ? t("tasks.view.columnCheckInDate")
                          : t("tasks.view.columnDueDate")}
                    </th>
                    {tab === VIDEO && (
                      <th className="video-task-col">
                        {t("tasks.view.columnVideos")}
                      </th>
                    )}
                    <th className="col edit-col">
                      {t("tasks.view.columnAction")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {showTaskList(
                    thisWeek,
                    tab,
                    this.handleSelectTask,
                    selectedTasks,
                    organizationCoach,
                    screenHeight,
                    isAllowToViewAllOrganizationsCoaches,
                    activeUserId,
                    t
                  )}
                  {upcomingLength > 0 && (
                    <div className="list-title upcoming-task">
                      {t("tasks.view.upcoming", { count: upcomingLength })}
                    </div>
                  )}
                  {showTaskList(
                    upcoming,
                    tab,
                    this.handleSelectTask,
                    selectedTasks,
                    organizationCoach,
                    screenHeight,
                    isAllowToViewAllOrganizationsCoaches,
                    activeUserId,
                    t
                  )}
                  {emptyRows.map((rowKey) => (
                    <tr key={rowKey}>
                      <td>
                        <div className="h-8"></div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td></td>
                      {tab === VIDEO && <td></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : (
            <EmptyTasks
              tab={tab}
              organizationCoach={organizationCoach}
              selectedTasks={selectedTasks}
              referAsClients={referAsClients}
            />
          )}
        </section>
      </main>
    );
  }
}

View.contextType = RillContext;

export default withTranslation("coaching-portal")(View);
